import { EventEmitter } from 'events';
import {
  type Card,
  type CardInputEvent,
  type Payments,
  type SqEvent,
  type TokenError,
} from '@square/web-payments-sdk-types';
import { type CreditCardNetwork } from '@wix/cashier-common/dist/src/enums/payments/CreditCardNetwork';
import { win } from '@wix/cashier-common/dist/src/utils/win';
import { waitForCondition } from '@wix/cashier-common/dist/src/utils/waitForCondition';
import { type CardPublicData } from '../../types/CardPublicData';
import { type DeviceType } from '../../types/DeviceType';
import { type Expiration } from '../../types/Expiration';
import { FieldName } from '../../types/CardFieldName';
import { type Theme } from '../../types/Theme';
import { type SqPaymentPublicCard, type SqVerificationDetails } from './types';
import {
  getSquareWebSDKInputStyles,
  internalToSquareFieldNamesMap,
  squareToInternalCardNetworkMap,
  squareToInternalFieldNamesMap,
  squareTokenizeToInternalCardNetworkMap,
} from './utils';
import { squareFormContainerId } from './constants';

interface SquareInitConfig {
  applicationId: string;
  locationId: string;
  deviceType: DeviceType;
  theme: Theme;
}

export const enum SquareFormEvent {
  FieldChange = 'fieldChange',
  FieldFocus = 'fieldFocus',
  CardNetworkChange = 'cardNetworkChange',
  TokenizeDone = 'tokenizeDone',
  InitChange = 'initChange',
}

// https://developer.squareup.com/docs/payment-form/payment-form-walkthrough
export class SquareFormAPI {
  private initPromise: Promise<void>;
  private deviceType: DeviceType;
  private publicData: CardPublicData = null;
  // Null need for track right initial state of PostalCode presence
  // Trigger emitPostalCodeChangeOnHide in case of user type card without PostalCode (by default PC have isValid:false)
  private isPostalCodeFieldPresence: null | boolean = null;
  private isPostalCodeFieldEmpty = true;
  public squarePaymentsInstance: Payments;
  public squareCardInstance: Card;
  public emitter: EventEmitter = new EventEmitter();

  private async initSqWebPaymentSDK(config: SquareInitConfig) {
    this.emitter.emit(SquareFormEvent.InitChange, false);
    this.deviceType = config.deviceType;

    const handleFieldChange = (
      e: SqEvent<CardInputEvent>,
      isValid: boolean = e.detail.currentState.isCompletelyValid,
    ) => {
      const elementId = squareToInternalFieldNamesMap[e.detail.field];
      this.publicData = null;

      this.emitter.emit(SquareFormEvent.FieldChange, elementId, isValid, e.detail.currentState.isEmpty);
    };

    const getIsPostalCodePresence = (e: SqEvent<CardInputEvent>): boolean => e.detail.hasOwnProperty('postalCodeValue');

    const emitPostalCodeChangeOnHide = (): void => {
      // If postal code gets hidden - set to our validity form next state => postalCode: [isValid: true, isEmpty: lastIsEmptyValue]
      this.emitter.emit(SquareFormEvent.FieldChange, FieldName.PostalCode, true, this.isPostalCodeFieldEmpty);
    };

    const trackPostaleCodePresence = (e: SqEvent<CardInputEvent>, runAfterUpdate: () => void): void => {
      const isPostalCodeFieldPresence = getIsPostalCodePresence(e);

      // If value doesn't changed - Do nothing (Handler can be fired when PC presence doesn't changed)
      if (this.isPostalCodeFieldPresence === isPostalCodeFieldPresence) {
        return;
      }

      this.isPostalCodeFieldPresence = isPostalCodeFieldPresence;

      runAfterUpdate();
    };

    const commonHandlerPostalCodeValidity = (e: SqEvent<CardInputEvent>): void => {
      trackPostaleCodePresence(e, () => {
        // If PC is hidden we must make sure it's isValid state === true; Because by default PC isValid === false
        // In other case we should only track current state of PC presence
        if (!getIsPostalCodePresence(e)) {
          emitPostalCodeChangeOnHide();
        }
      });
    };

    const postalCodeChangeHandler = (e: SqEvent<CardInputEvent>): void => {
      // Need for more correct emitPostalCodeChangeOnHide event
      this.isPostalCodeFieldEmpty = e.detail.currentState.isEmpty;

      // If PC presence - call base fieldOnChange handler
      if (getIsPostalCodePresence(e)) {
        handleFieldChange(e);
        return;
      }

      // If PC go to hide
      trackPostaleCodePresence(e, emitPostalCodeChangeOnHide);
    };

    return (this.initPromise = new Promise<void>(async (resolve) => {
      await waitForCondition(() => !!win.Square, { errorMessage: `Square SDK wasn't found`, timeout: 5000 });
      this.squarePaymentsInstance = win.Square.payments(config.applicationId, config.locationId);

      this.squareCardInstance = await this.squarePaymentsInstance.card({
        style: getSquareWebSDKInputStyles(this.deviceType, config.theme),
      });

      this.squareCardInstance.addEventListener('focusClassAdded', (e) => {
        const fieldName = squareToInternalFieldNamesMap[e.detail.field];
        this.emitter.emit(`${SquareFormEvent.FieldFocus}:${fieldName}`, true);
      });
      this.squareCardInstance.addEventListener('focusClassRemoved', (e) => {
        const fieldName = squareToInternalFieldNamesMap[e.detail.field];
        commonHandlerPostalCodeValidity(e);
        handleFieldChange(e); // need change data before run onBlur()
        this.emitter.emit(`${SquareFormEvent.FieldFocus}:${fieldName}`, false);
      });
      this.squareCardInstance.addEventListener('errorClassAdded', (e) => {
        commonHandlerPostalCodeValidity(e);
        handleFieldChange(e);
      });
      this.squareCardInstance.addEventListener('errorClassRemoved', (e) => {
        commonHandlerPostalCodeValidity(e);
        handleFieldChange(e);
      });
      this.squareCardInstance.addEventListener('postalCodeChanged', (e) => {
        postalCodeChangeHandler(e);
      });
      this.squareCardInstance.addEventListener('cardBrandChanged', (e) => {
        this.emitter.emit(SquareFormEvent.CardNetworkChange, squareToInternalCardNetworkMap[e.detail.cardBrand]);
      });

      await this.squareCardInstance.attach(`#${squareFormContainerId}`);
      resolve(); // finish initialization (input is added to the DOM)
      this.emitter.emit(SquareFormEvent.InitChange, true);
    }));
  }

  private async tokenizeCardData(verificationDetails: SqVerificationDetails) {
    try {
      const tokenResult = await this.squareCardInstance.tokenize(verificationDetails);

      if (tokenResult.status === 'OK') {
        this.emitter.emit(SquareFormEvent.TokenizeDone, {
          token: tokenResult.token,
          publicData: {
            card_brand: tokenResult.details.card.brand,
            last_4: tokenResult.details.card.last4,
            exp_month: tokenResult.details.card.expMonth,
            exp_year: tokenResult.details.card.expYear, // the 4-digit year of the expiration date
          } as SqPaymentPublicCard,
        });
        return;
      }

      this.emitter.emit(SquareFormEvent.TokenizeDone, {
        token: tokenResult.token,
        errors: tokenResult.errors,
      });
    } catch ({ errors }) {
      this.emitter.emit(SquareFormEvent.TokenizeDone, {
        token: null,
        errors,
      });
    }
  }

  public async init(config: SquareInitConfig) {
    return this.initSqWebPaymentSDK(config);
  }

  public getPublicData(): CardPublicData {
    return this.publicData;
  }

  public listenFieldChange(cb: (fieldName: FieldName, isValid: boolean, isEmpty: boolean) => void) {
    this.emitter.on(SquareFormEvent.FieldChange, cb);
  }

  public unlistenFieldChange(cb: (fieldName: FieldName, isValid: boolean, isEmpty: boolean) => void) {
    this.emitter.removeListener(SquareFormEvent.FieldChange, cb);
  }

  public listenCardNetworkChange(cb: (cardNetwork: CreditCardNetwork) => void) {
    this.emitter.on(SquareFormEvent.CardNetworkChange, cb);
  }

  public unlistenCardNetworkChange(cb: (cardNetwork: CreditCardNetwork) => void) {
    this.emitter.removeListener(SquareFormEvent.CardNetworkChange, cb);
  }

  public listenFieldFocus(fieldName: FieldName, cb: (isFocused: boolean) => void) {
    this.emitter.on(`${SquareFormEvent.FieldFocus}:${fieldName}`, cb);
  }

  public unlistenFieldFocus(fieldName: FieldName, cb: (isFocused: boolean) => void) {
    this.emitter.removeListener(`${SquareFormEvent.FieldFocus}:${fieldName}`, cb);
  }

  public unlistenAllFieldFocus(fieldName: FieldName) {
    this.emitter.removeAllListeners(`${SquareFormEvent.FieldFocus}:${fieldName}`);
  }

  public listenInitChange(cb: (isInited: boolean) => void) {
    this.emitter.on(SquareFormEvent.InitChange, cb);
  }

  public unlistenInitChange(cb: (isInited: boolean) => void) {
    this.emitter.removeListener(SquareFormEvent.InitChange, cb);
  }

  public async changeDeviceType(deviceType: DeviceType): Promise<void> {
    if (!this.initPromise) {
      // if init wasn't called yet, then the correct device type will be using during initial init
      return;
    }

    this.deviceType = deviceType;

    if (this.squareCardInstance) {
      this.squareCardInstance.recalculateSize();
    }
  }

  public focusField(fieldName: FieldName): void {
    this.squareCardInstance.focus(internalToSquareFieldNamesMap[fieldName]);
  }

  public tokenize = (
    verificationDetails?: SqVerificationDetails,
  ): Promise<{
    numberToken: string;
    expiration: Expiration;
  }> => {
    return new Promise<{
      numberToken: string;
      expiration: Expiration;
    }>((resolve, reject) => {
      const responseListener = (data: { errors?: TokenError[]; token: string; publicData?: SqPaymentPublicCard }) => {
        const { token, errors, publicData } = data;

        if (errors) {
          reject(Array.isArray(errors) ? errors[0] : errors);
        } else {
          this.publicData = {
            lastFourDigits: publicData.last_4,
            expiration: {
              year: typeof publicData.exp_year === 'string' ? parseInt(publicData.exp_year, 10) : publicData.exp_year,
              month:
                typeof publicData.exp_month === 'string' ? parseInt(publicData.exp_month, 10) : publicData.exp_month,
            },
            type: squareTokenizeToInternalCardNetworkMap[publicData.card_brand],
          };
          resolve({
            numberToken: token,
            expiration: this.publicData.expiration,
          });
        }

        this.emitter.removeListener(SquareFormEvent.TokenizeDone, responseListener);
      };

      this.emitter.on(SquareFormEvent.TokenizeDone, responseListener);

      this.tokenizeCardData(verificationDetails); // can throw error - SqTokenizationError
    });
  };
}
